import React, { FC, PropsWithChildren, ReactNode } from 'react'

import { v4 as uuidV4 } from 'uuid'

type Props = {
  substrings: string[]
  isCaseSensitive?: boolean
}

/** substringsに該当する文字を太字にする */
export const BoldSubstrings: FC<PropsWithChildren<Props>> = ({
  children,
  substrings,
  isCaseSensitive = false,
}) => {
  // 部分文字列を太字にする関数
  const bolden = (childString: string, targets: string[]): ReactNode[] => {
    // 対象の部分文字列がない場合、そのまま文字列を返す
    if (targets.length === 0) {
      return [childString]
    }

    // 現在の対象の部分文字列と残りを取得
    const [target, ...rest] = targets

    // target 内の正規表現に関する特殊文字をエスケープし、次の処理で Invalid regular expression エラーが起きないようにする
    const escapedTarget = target.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')
    // isCaseSensitiveフラグがtrueの場合は大文字小文字を区別してマッチさせる
    const regex = new RegExp(escapedTarget, isCaseSensitive ? 'g' : 'ig')

    // 正規表現に一致する部分をすべて取得
    const matches = [...childString.matchAll(regex)]
    // 一致する部分がない場合、次のターゲットに移動
    if (matches.length === 0) {
      return bolden(childString, rest)
    }

    let lastIndex = 0
    // 変換後の要素を格納する配列
    const results: ReactNode[] = []

    // 一致する部分ごとに処理
    for (const match of matches) {
      // 一致する部分の前の文字列を再帰的に太字にする
      results.push(
        bolden(childString.slice(lastIndex, match.index), rest),
        <strong key={uuidV4()}>{match[0]}</strong>
      )
      // 次の処理開始位置を更新
      lastIndex = match.index ? match.index + match[0].length : match[0].length
    }

    // 最後の一致部分の後の文字列を追加
    results.push(bolden(childString.slice(Math.max(0, lastIndex)), rest))

    // 変換後の要素の配列を返す
    return results.flat()
  }

  return (
    <>
      {React.Children.map(children, child => {
        if (typeof child === 'string') {
          return bolden(child, substrings)
        }
        return child // 他のReactNodeの型の場合、そのまま返す
      })}
    </>
  )
}
